/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: AuthenticationCodesPage.tsx
 ??? Created: 2025-01-28
 ?? Description:
    Authentication Rules list page with table view, search, pagination, and actions.
    Allows viewing, creating, duplicating, editing, and deleting authentication rules.

 ?? Notes:
    - Domain-scoped (multi-tenant safe)
    - Supports search and pagination
    - Shows name, context, code count, and enabled status

 ?? Last Updated: 2025-11-07 by GPT-5 Codex
----------------------------------------------------------------*/

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Edit, Key, Trash2 } from 'lucide-react';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import DataGridSimple from '../../components/ui/DataGridSimple';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigationStore } from '../../store/navigation';
import { deleteAuthenticationRule } from './api';
import { navigateToAuthenticationRuleDuplicate } from './authenticationCodesActions';
import { useAuthenticationRuleList } from './hooks';
import { AuthenticationRule } from './types';

const ACTION_BUTTON_CLASSNAMES = {
  base: 'p-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60',
  edit: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
  duplicate: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/30',
  delete: 'hover:bg-red-100 dark:hover:bg-red-900/30',
};

export default function AuthenticationCodesPage() {
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();

  const pageTitle = useMemo(() => t('authenticationCodes.title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; rule: AuthenticationRule | null }>({
    isOpen: false,
    rule: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const availableColumns = useMemo(() => {
    return [
      {
        key: 'name',
        label: t('authenticationCodes.columns.name'),
        visible: true,
        sortable: true,
      },
      {
        key: 'context',
        label: t('authenticationCodes.columns.context'),
        visible: true,
        sortable: true,
      },
      {
        key: 'codeCount',
        label: t('authenticationCodes.columns.codeCount'),
        visible: true,
        sortable: false,
      },
      {
        key: 'destination',
        label: t('authenticationCodes.columns.destination'),
        visible: true,
        sortable: false,
      },
      {
        key: 'enabled',
        label: t('authenticationCodes.columns.enabled'),
        visible: true,
        sortable: true,
      },
      {
        key: 'createdAt',
        label: t('common:createdAt'),
        visible: false,
        sortable: true,
      },
      {
        key: 'actions',
        label: t('common:actions'),
        visible: true,
        sortable: false,
      },
    ];
  }, [t]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useAuthenticationRuleList({ initialColumns: availableColumns });

  const handleCreate = () => {
    navigate('/pbx/incoming-tools/auth-codes/new');
  };

  const handleEdit = (rule: AuthenticationRule) => {
    navigate(`/pbx/incoming-tools/auth-codes/${rule.uuid ?? rule.id}`);
  };

  const handleDuplicate = (rule: AuthenticationRule) => {
    navigateToAuthenticationRuleDuplicate(navigate, rule.uuid ?? rule.id);
  };

  const handleDeleteClick = useCallback((rule: AuthenticationRule) => {
    setDeleteModal({ isOpen: true, rule });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.rule) return;

    setIsDeleting(true);
    try {
      await deleteAuthenticationRule(deleteModal.rule.id);
      showSuccess(t('authenticationCodes.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, rule: null });
      refresh();
    } catch (error) {
      showError(error.message || t('authenticationCodes.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.rule, refresh, showError, showSuccess, t]);

  const formatDestinationData = useCallback(
    (module: string, data: string): string => {
      if (module === 'terminate_call') {
        // Try to translate the terminate cause code
        const translated = t(`authenticationCodes.terminateCauses.${data}.label`, { defaultValue: data });
        return translated || data;
      }
      return data;
    },
    [t],
  );

  const renderActions = useCallback(
    (rule: AuthenticationRule) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('common:edit')} position="top">
          <button
            type="button"
            onClick={() => handleEdit(rule)}
            aria-label={t('common:edit')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.edit}`}
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common:duplicate')} position="top">
          <button
            type="button"
            onClick={() => handleDuplicate(rule)}
            aria-label={t('common:duplicate')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.duplicate}`}
          >
            <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common:delete')} position="top">
          <button
            type="button"
            onClick={() => handleDeleteClick(rule)}
            aria-label={t('common:delete')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.delete}`}
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleDeleteClick, handleDuplicate, handleEdit, t],
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            data={data}
            columns={columns.map((column) => ({
              ...column,
              render:
                column.key === 'name'
                  ? (_value, item: AuthenticationRule) => (
                    <span className="font-medium text-foreground">{item.name}</span>
                  )
                  : column.key === 'context'
                    ? (_value, item: AuthenticationRule) => (
                      <span className="font-mono text-sm text-muted-foreground">{item.context}</span>
                    )
                    : column.key === 'codeCount'
                      ? (_value, item: AuthenticationRule) => (
                        <div className="text-sm">
                          <div className="font-medium text-foreground">
                            {item.codeCount} {t('authenticationCodes.fields.codeCount.total')}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {item.enabledCodeCount} {t('authenticationCodes.fields.codeCount.enabled')}
                          </div>
                        </div>
                      )
                      : column.key === 'destination'
                        ? (_value, item: AuthenticationRule) => (
                          <div className="text-sm">
                            <div className="font-medium text-foreground">
                              {t('authenticationCodes.fields.destinationMatch.label')}: {t(`authenticationCodes.destinationModules.${item.destinationMatchModule}`, item.destinationMatchModule)} � {formatDestinationData(item.destinationMatchModule, item.destinationMatchData)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {t('authenticationCodes.fields.destinationFail.label')}: {t(`authenticationCodes.destinationModules.${item.destinationFailModule}`, item.destinationFailModule)} � {formatDestinationData(item.destinationFailModule, item.destinationFailData)}
                            </div>
                          </div>
                        )
                        : column.key === 'enabled'
                          ? (_value, item: AuthenticationRule) => (
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${item.enabled
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
                                }`}
                            >
                              {item.enabled ? t('common:enabled') : t('common:disabled')}
                            </span>
                          )
                          : column.key === 'createdAt'
                            ? (_value, item: AuthenticationRule) => {
                              const date = new Date(item.createdAt);
                              const isValid = !Number.isNaN(date.getTime());
                              return (
                                <span className="text-sm text-muted-foreground">
                                  {isValid ? date.toLocaleDateString() : t('common:notAvailable')}
                                </span>
                              );
                            }
                            : column.key === 'actions'
                              ? (_value, item: AuthenticationRule) => renderActions(item)
                              : column.render,
            }))}
            loading={loading}
            pagination={pagination}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSearch={handleSearch}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onColumnToggle={handleColumnToggle}
            onAdd={handleCreate}
            title={pageTitle}
            titleIcon={<Key className="h-5 w-5 text-primary" />}
            addButtonText={t('common:add')}
            searchPlaceholder={t('authenticationCodes.searchPlaceholder')}
            emptyMessage={t('authenticationCodes.noResults')}
          />

          <ConfirmationModal
            isOpen={deleteModal.isOpen}
            onClose={() => setDeleteModal({ isOpen: false, rule: null })}
            onConfirm={handleDeleteConfirm}
            title={t('authenticationCodes.deleteEntry')}
            message={t('authenticationCodes.messages.deleteConfirmation', {
              name: deleteModal.rule?.name ?? '',
            })}
            confirmText={t('common:delete')}
            cancelText={t('common:cancel')}
            type="danger"
            isLoading={isDeleting}
          />
        </>
      }
      formView={null}
    />
  );
}

